<template>
  <div class="main-content">
    <!-- Header -->
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="card">
          <div
            class="card-body py-2 d-flex align-items-center justify-content-between"
          >
            <div>
              <h4 class="mb-0">
                <i class="fas fa-code-branch text-primary mr-2"></i>
                <strong>{{ release.version_string }}</strong>
                <span class="ml-2">{{ release.name }}</span>
                <template v-if="isAdmin">
                  <v-select
                    v-model="release.status"
                    :options="statusOptions"
                    :reduce="(s) => s.value"
                    label="label"
                    :clearable="false"
                    class="d-inline-block ml-2"
                    style="min-width: 180px; vertical-align: middle"
                    @input="updateStatus"
                  ></v-select>
                </template>
                <span
                  v-else
                  :class="statusBadge(release.status)"
                  class="ml-2"
                  >{{ statusLabel(release.status) }}</span
                >
              </h4>
              <small class="text-muted" v-if="release.project">
                Sistema: {{ release.project.name }}
              </small>
            </div>
            <div>
              <button class="btn btn-secondary mr-2" @click="$router.back()">
                <i class="fas fa-arrow-left mr-1"></i> Voltar
              </button>
              <router-link
                v-if="isAdmin"
                :to="`/projects/${projectId}/releases/edit/${releaseId}`"
                class="btn btn-warning mr-2"
              >
                <i class="fas fa-pencil-alt mr-1"></i> Editar
              </router-link>
              <button class="btn btn-success" @click="downloadPdf">
                <i class="fas fa-file-pdf mr-1"></i> Gerar PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info cards -->
    <div class="row mb-3">
      <div class="col-md-8">
        <div class="card h-100">
          <div class="card-header"><strong>Datas</strong></div>
          <div class="card-body">
            <table class="table table-sm table-bordered mb-0">
              <tbody>
                <tr>
                  <th class="bg-light" style="width: 40%">
                    Data Fim do Desenvolvimento
                  </th>
                  <td>{{ release.data_fim_desenvolvimento_f || "—" }}</td>
                </tr>
                <tr>
                  <th class="bg-light">Data Prevista Início dos Testes</th>
                  <td>{{ release.data_prevista_inicio_testes_f || "—" }}</td>
                </tr>
                <tr>
                  <th class="bg-light">Data Fim dos Testes</th>
                  <td>{{ release.data_fim_testes_f || "—" }}</td>
                </tr>
                <tr>
                  <th class="bg-light">Programação Inicial de Deploy</th>
                  <td>{{ release.programacao_inicial_deploy_f || "—" }}</td>
                </tr>
                <tr>
                  <th class="bg-light">Data Fechamento da Release</th>
                  <td>{{ release.data_fechamento_release_f || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-header"><strong>Responsáveis</strong></div>
          <div class="card-body">
            <p class="mb-1">
              <strong>Relator:</strong>
              {{ release.relator ? release.relator.name : "—" }}
            </p>
            <p class="mb-1">
              <strong>P.O:</strong>
              {{ release.po ? release.po.name : "—" }}
            </p>
            <p class="mb-1">
              <strong>QA:</strong>
              <span v-if="release.qa_users && release.qa_users.length">
                {{ release.qa_users.map((u) => u.name).join(", ") }}
              </span>
              <span v-else>—</span>
            </p>
            <p class="mb-0">
              <strong>Devs:</strong>
              <span v-if="release.dev_users && release.dev_users.length">
                {{ release.dev_users.map((u) => u.name).join(", ") }}
              </span>
              <span v-else>—</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Iniciar Testes -->
    <div class="row mb-3" v-if="release.status === 'aguardando_testes' && isQaUser()">
      <div class="col-md-12">
        <div class="card border-info">
          <div class="card-body d-flex align-items-center justify-content-between py-3">
            <div>
              <h5 class="mb-1 text-info">
                <i class="fas fa-flask mr-2"></i> Todos os chamados estão prontos para teste
              </h5>
              <p class="mb-0 text-muted" style="font-size: 13px">
                Como QA responsável, clique em "Iniciar Testes" para registrar o início da fase de testes.
              </p>
            </div>
            <button
              class="btn btn-info"
              @click="startTests"
              :disabled="startingTests"
            >
              <i class="fas fa-play mr-1"></i>
              {{ startingTests ? 'Iniciando...' : 'Iniciar Testes' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Aprovação QA -->
    <div class="row mb-3" v-if="release.status === 'aguardando_aprovacao_qa' && isQaUser()">
      <div class="col-md-12">
        <div class="card border-warning">
          <div class="card-header bg-warning text-white">
            <strong><i class="fas fa-user-check mr-2"></i> Aprovação QA Necessária</strong>
          </div>
          <div class="card-body">
            <p class="mb-3 text-muted">
              Todos os chamados estão em <strong>Aguardando Deploy</strong>. Revise a release e, caso esteja aprovada, anexe o documento de aprovação e confirme.
            </p>
            <div class="form-group mb-3">
              <label class="font-weight-bold">Documento de Aprovação (PDF) <span class="text-danger">*</span></label>
              <input
                type="file"
                class="form-control-file"
                accept=".pdf"
                @change="onQaFileChange"
              />
              <small class="text-muted">Apenas arquivos PDF. Máx. 20MB.</small>
            </div>
            <button
              class="btn btn-success"
              @click="submitQaApproval"
              :disabled="approvingQa || !qaApprovalFile"
            >
              <i class="fas fa-check-circle mr-1"></i>
              {{ approvingQa ? 'Aprovando...' : 'Aprovar Release' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Info de Aprovação QA (pós-aprovação) -->
    <div class="row mb-3" v-if="release.qa_approved_at">
      <div class="col-md-12">
        <div class="card border-success">
          <div class="card-body py-2 d-flex align-items-center justify-content-between">
            <div>
              <strong class="text-success"><i class="fas fa-check-circle mr-2"></i> Aprovada pelo QA</strong>
              <span class="ml-3 text-muted" style="font-size: 13px">
                Por <strong>{{ release.qa_approver ? release.qa_approver.name : '—' }}</strong>
                em {{ release.qa_approved_at_f }}
              </span>
            </div>
            <a
              v-if="release.qa_approval_document"
              :href="qaDocumentUrl()"
              target="_blank"
              class="btn btn-sm btn-outline-success"
            >
              <i class="fas fa-file-pdf mr-1"></i> Ver Documento
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Observações -->
    <div class="row mb-3" v-if="release.description">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header"><strong>Observações</strong></div>
          <div class="card-body">
            <p class="mb-0">{{ release.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Chamados vinculados -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <strong>Chamados Vinculados ({{ tickets.length }})</strong>
            <button
              v-if="isAdmin"
              class="btn btn-primary btn-sm"
              @click="openAddModal"
            >
              <i class="fas fa-plus mr-1"></i> Adicionar Chamado
            </button>
          </div>
          <div class="card-body p-0">
            <div v-if="loadingTickets" class="text-center py-3">
              <i class="fas fa-spinner fa-spin text-primary"></i>
            </div>
            <table
              v-else-if="tickets.length > 0"
              class="table table-sm table-hover mb-0"
            >
              <thead class="thead-light">
                <tr>
                  <th style="width: 60px">#</th>
                  <th>Título</th>
                  <th>Status</th>
                  <th>Responsável</th>
                  <th class="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ticket in tickets" :key="ticket.id">
                  <td>
                    <strong>{{ ticket.id }}</strong>
                  </td>
                  <td>{{ ticket.title || ticket.description_error }}</td>
                  <td>
                    <span
                      v-if="ticket.status_calls"
                      :class="ticket.status_calls.badge_description"
                    >
                      {{ ticket.status_calls.description }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td>{{ ticket.dev ? ticket.dev.name : "—" }}</td>
                  <td class="text-center">
                    <router-link
                      :to="`/calls/ticket/${ticket.id}`"
                      class="btn btn-sm btn-crud view"
                      title="Abrir chamado"
                      target="_blank"
                    >
                      <i class="fas fa-eye"></i>
                    </router-link>
                    <button
                      v-if="isAdmin"
                      class="btn btn-sm btn-crud delete ml-1"
                      title="Desvincular"
                      @click="detachTicket(ticket)"
                    >
                      <i class="fas fa-unlink"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center py-3 text-muted">
              <i class="fas fa-ticket-alt fa-2x mb-1"></i>
              <p class="mb-0">Nenhum chamado vinculado.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Adicionar Chamado -->
    <div
      class="modal fade"
      id="addTicketModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="addTicketModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addTicketModalLabel">
              Adicionar Chamado à Release
            </h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Buscar por título ou ID</label>
              <input
                type="text"
                class="form-control"
                v-model="ticketSearch"
                placeholder="Digite para buscar..."
                @input="searchTickets"
              />
            </div>
            <div v-if="loadingSearch" class="text-center py-2">
              <i class="fas fa-spinner fa-spin text-primary"></i>
            </div>
            <table
              v-else-if="availableTickets.length > 0"
              class="table table-sm table-hover"
            >
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th>Status</th>
                  <th>Release Atual</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ticket in availableTickets" :key="ticket.id">
                  <td>{{ ticket.id }}</td>
                  <td>{{ ticket.title || ticket.description_error }}</td>
                  <td>
                    <span
                      v-if="ticket.status_calls"
                      :class="ticket.status_calls.badge_description"
                      style="font-size: 11px"
                    >
                      {{ ticket.status_calls.description }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="ticket.release"
                      class="badge badge-warning"
                      style="font-size: 11px"
                    >
                      {{ ticket.release.version_string }} -
                      {{ ticket.release.name }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <button
                      class="btn btn-sm btn-primary"
                      @click="attachTicket(ticket)"
                      :disabled="attachingId === ticket.id"
                    >
                      <i class="fas fa-link"></i>
                      {{ attachingId === ticket.id ? "..." : "Vincular" }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              v-else-if="ticketSearch.length >= 2"
              class="text-center text-muted py-2"
            >
              Nenhum chamado encontrado.
            </div>
            <div v-else class="text-muted" style="font-size: 13px">
              Digite ao menos 2 caracteres para buscar chamados do sistema
              <strong>{{ release.project ? release.project.name : "" }}</strong
              >.
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      release: {},
      tickets: [],
      availableTickets: [],
      ticketSearch: "",
      loadingTickets: false,
      loadingSearch: false,
      attachingId: null,
      searchTimeout: null,
      qaApprovalFile: null,
      approvingQa: false,
      startingTests: false,
      statusOptions: [
        { value: "planejada", label: "Planejada" },
        { value: "em_desenvolvimento", label: "Em Desenvolvimento" },
        { value: "em_testes", label: "Em Testes" },
        { value: "fechada", label: "Fechada" },
        { value: "finalizada", label: "Finalizada" },
        { value: "planejamento_atrasado", label: "Planejamento Atrasado" },
        { value: "desenvolvimento_atrasado", label: "Desenvolvimento Atrasado" },
        { value: "aguardando_testes", label: "Aguardando Testes" },
        { value: "testes_atrasados", label: "Testes Atrasados" },
        { value: "aguardando_aprovacao_qa", label: "Aguardando Aprovação QA" },
        { value: "aguardando_deploy", label: "Aguardando Deploy" },
        { value: "deploy_atrasado", label: "Deploy Atrasado" },
      ],
    };
  },

  computed: {
    projectId() {
      return this.$route.params.project_id;
    },
    releaseId() {
      return this.$route.params.id;
    },
    isAdmin() {
      return this.$store.state.user.perfil_id === 8;
    },
  },

  mounted() {
    this.getRelease();
    this.getTickets();
  },

  methods: {
    getRelease() {
      axios
        .get(
          this.$store.state.api +
            `releases/${this.releaseId}?with[]=project&with[]=relator&with[]=po&with[]=qaUsers&with[]=devUsers&with[]=qaApprover`
        )
        .then((res) => {
          this.release = res.data;
        })
        .catch(() => {
          this.$message(
            "Erro",
            "Não foi possível carregar a release.",
            "error"
          );
        });
    },

    getTickets() {
      this.loadingTickets = true;
      axios
        .get(this.$store.state.api + `releases/${this.releaseId}/tickets`)
        .then((res) => {
          this.tickets = res.data.data || res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingTickets = false;
        });
    },

    statusLabel(status) {
      const map = {
        planejada: "Planejada",
        em_desenvolvimento: "Em Desenvolvimento",
        em_testes: "Em Testes",
        fechada: "Fechada",
        finalizada: "Finalizada",
        planejamento_atrasado: "Planejamento Atrasado",
        desenvolvimento_atrasado: "Desenvolvimento Atrasado",
        aguardando_testes: "Aguardando Testes",
        testes_atrasados: "Testes Atrasados",
        aguardando_aprovacao_qa: "Aguardando Aprovação QA",
        aguardando_deploy: "Aguardando Deploy",
        deploy_atrasado: "Deploy Atrasado",
      };
      return map[status] || status;
    },

    statusBadge(status) {
      const map = {
        planejada: "badge badge-secondary",
        em_desenvolvimento: "badge badge-primary",
        em_testes: "badge badge-warning",
        fechada: "badge badge-success",
        finalizada: "badge badge-info",
        planejamento_atrasado: "badge badge-danger",
        desenvolvimento_atrasado: "badge badge-danger",
        aguardando_testes: "badge badge-info",
        testes_atrasados: "badge badge-danger",
        aguardando_aprovacao_qa: "badge badge-warning",
        aguardando_deploy: "badge badge-primary",
        deploy_atrasado: "badge badge-danger",
      };
      return map[status] || "badge badge-secondary";
    },

    isQaUser() {
      const userId = this.$store.state.user.id;
      return this.release.qa_users && this.release.qa_users.some(u => u.id === userId);
    },

    startTests() {
      this.startingTests = true;
      axios
        .post(this.$store.state.api + `releases/${this.releaseId}/start-tests`)
        .then(() => {
          this.$message("Sucesso", "Testes iniciados! Status atualizado para Em Testes.", "success");
          this.getRelease();
        })
        .catch(err => {
          const msg = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Não foi possível iniciar os testes.";
          this.$message("Erro", msg, "error");
        })
        .finally(() => {
          this.startingTests = false;
        });
    },

    onQaFileChange(event) {
      this.qaApprovalFile = event.target.files[0] || null;
    },

    submitQaApproval() {
      if (!this.qaApprovalFile) {
        this.$message("Atenção", "Anexe o documento PDF de aprovação.", "warning");
        return;
      }
      this.approvingQa = true;
      const formData = new FormData();
      formData.append("pdf", this.qaApprovalFile);

      axios
        .post(this.$store.state.api + `releases/${this.releaseId}/qa-approve`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(res => {
          this.release = res.data;
          this.qaApprovalFile = null;
          this.$message("Sucesso", "Release aprovada pelo QA! Status: Aguardando Deploy.", "success");
        })
        .catch(err => {
          const msg = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Não foi possível registrar a aprovação.";
          this.$message("Erro", msg, "error");
        })
        .finally(() => {
          this.approvingQa = false;
        });
    },

    qaDocumentUrl() {
      if (!this.release.qa_approval_document) return null;
      return this.$store.state.storage_url + this.release.qa_approval_document;
    },

    openAddModal() {
      this.ticketSearch = "";
      this.availableTickets = [];
      $("#addTicketModal").modal("show");
    },

    searchTickets() {
      clearTimeout(this.searchTimeout);
      if (this.ticketSearch.length < 2) {
        this.availableTickets = [];
        return;
      }
      this.searchTimeout = setTimeout(() => {
        this.loadingSearch = true;
        const query = encodeURIComponent(this.ticketSearch);
        axios
          .get(
            this.$store.state.api +
              `calls?system_error_id=${this.release.project_id}&title=${query}&paginated=false&with[]=status_calls&with[]=release&exclude_status=9,12`
          )
          .then((res) => {
            this.availableTickets = (res.data.data || res.data).filter(
              (t) => t.release_id !== this.releaseId
            );
          })
          .catch(() => {})
          .finally(() => {
            this.loadingSearch = false;
          });
      }, 400);
    },

    attachTicket(ticket) {
      this.attachingId = ticket.id;
      axios
        .post(this.$store.state.api + "releases/attach-ticket", {
          ticket_id: ticket.id,
          release_id: this.releaseId,
        })
        .then(() => {
          this.getTickets();
          this.availableTickets = this.availableTickets.filter(
            (t) => t.id !== ticket.id
          );
          this.$message(
            "Sucesso",
            `Chamado #${ticket.id} vinculado!`,
            "success"
          );
        })
        .catch(() => {
          this.$message(
            "Erro",
            "Não foi possível vincular o chamado.",
            "error"
          );
        })
        .finally(() => {
          this.attachingId = null;
        });
    },

    detachTicket(ticket) {
      swal({
        title: `Desvincular chamado #${ticket.id}?`,
        type: "warning",
        buttons: {
          cancel: { text: "Não", value: "cancel", className: "btn-danger" },
          confirm: { text: "Sim", value: "confirm" },
        },
      }).then((val) => {
        if (val === "confirm") {
          axios
            .delete(
              this.$store.state.api + `releases/detach-ticket/${ticket.id}`
            )
            .then(() => {
              this.tickets = this.tickets.filter((t) => t.id !== ticket.id);
              this.$message("Sucesso", "Chamado desvinculado!", "success");
            })
            .catch(() => {
              this.$message("Erro", "Não foi possível desvincular.", "error");
            });
        }
      });
    },

    downloadPdf() {
      axios
        .get(this.$store.state.api + `releases/${this.releaseId}/pdf`, {
          responseType: "blob",
        })
        .then((res) => {
          const disposition = res.headers["content-disposition"] || "";
          const match = disposition.match(
            /filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/
          );
          const fileName = match ? match[2] : `release_${this.releaseId}.pdf`;
          const blob = new Blob([res.data], { type: "application/pdf" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch(() => {
          this.$message("Erro", "Não foi possível gerar o PDF.", "error");
        });
    },

    updateStatus(newStatus) {
      axios
        .put(this.$store.state.api + `releases/${this.releaseId}`, {
          status: newStatus,
        })
        .then(() => {
          this.$message("Sucesso", "Status atualizado!", "success");
        })
        .catch(() => {
          this.$message(
            "Erro",
            "Não foi possível atualizar o status.",
            "error"
          );
        });
    },
  },
};
</script>
